import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import AuthBackHeader from '../../components/login-v2/AuthBackHeader';
import AuthPrimaryButton from '../../components/login-v2/AuthPrimaryButton';
import Timer from '../../components/common/Timer';
import CertDialog from '../../components/verification-v2/CertDialog';
import CertHelpSheet from '../../components/verification-v2/CertHelpSheet';
import UnderlineField from '../../components/verification-v2/UnderlineField';
import {
  ErrorWarnIcon,
  HintCheckIcon,
  NetworkErrorIcon,
  PhoneBadgeIcon,
  SuccessCheckIcon,
  UserBadgeIcon,
} from '../../components/verification-v2/icons';
import { AuthContext } from '../../context/AuthContext';
import {
  formatPhoneDisplay,
  markPhoneVerified,
  normalizePhone,
} from '../../services/phoneCert';

const MAX_CODE_ATTEMPTS = 5;
const RESEND_COOLDOWN_SEC = 30;
const OTP_MINUTES = 3;
const PRIVACY_NOTE =
  '야옹상회의 이벤트 당첨 안내와 기프티콘 등을 입력하신 번호로 보내드립니다. 번호는 안전하게 보관되며 이외의 목적과 용도로 사용되지 않습니다. 인증 메세지는 카카오톡에서 확인 가능합니다.';

function formatMmSs(totalSeconds) {
  const safe = Math.max(0, totalSeconds);
  const m = String(Math.floor(safe / 60)).padStart(2, '0');
  const s = String(safe % 60).padStart(2, '0');
  return `${m}:${s}`;
}

function isAlreadyVerifiedError(message = '') {
  return /이미|다른.?계정|등록된|사용.?중|연결된/.test(message);
}

/**
 * 기프트콘(야옹콘) 이용 시 휴대폰 본인인증 — 피그마 «기프티콘 인증 페이지»
 */
export default function VerificationV2Page() {
  const navigate = useNavigate();
  const location = useLocation();
  const { API_SERVER_DOMAIN, useAccessToken } = useContext(AuthContext);
  const contextToken = useAccessToken();

  const routeState = location.state || {};
  const accessToken = routeState.accessToken || contextToken;
  const redirectTo = routeState.redirectTo || '/coupons';
  const redirectState = routeState.redirectState;

  const [phoneNumber, setPhoneNumber] = useState('');
  const [code, setCode] = useState('');
  const [certKey, setCertKey] = useState('');
  const [phase, setPhase] = useState('phone'); // phone | code | done
  const [otpActive, setOtpActive] = useState(false);
  const [timerKey, setTimerKey] = useState(0);
  const [resendSec, setResendSec] = useState(0);
  const [codeError, setCodeError] = useState('');
  const [codeHint, setCodeHint] = useState(''); // success / resend message under code
  const [attemptUsed, setAttemptUsed] = useState(0); // failed verify count
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [changeConfirmOpen, setChangeConfirmOpen] = useState(false);
  const [alreadyUsedOpen, setAlreadyUsedOpen] = useState(false);
  const [networkErrorOpen, setNetworkErrorOpen] = useState(false);
  const [sendLimitOpen, setSendLimitOpen] = useState(false);

  const phoneDigits = useMemo(() => normalizePhone(phoneNumber), [phoneNumber]);
  const canSend = phoneDigits.length >= 10 && !sending;
  const remainingAttempts = Math.max(0, MAX_CODE_ATTEMPTS - attemptUsed);
  const canConfirm =
    phase === 'code' &&
    otpActive &&
    code.trim().length >= 6 &&
    remainingAttempts > 0 &&
    !confirming &&
    !codeError?.includes('초과');

  useEffect(() => {
    if (!accessToken) {
      navigate('/login', { replace: true });
    }
  }, [accessToken, navigate]);

  useEffect(() => {
    if (resendSec <= 0) return undefined;
    const id = setInterval(() => {
      setResendSec((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [resendSec]);

  const goBack = () => {
    if (phase === 'done') {
      finishAndContinue();
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/coupons', { replace: true });
    }
  };

  const finishAndContinue = () => {
    markPhoneVerified();
    navigate(redirectTo, {
      replace: true,
      state: redirectState,
    });
  };

  const resetToPhoneStep = () => {
    setPhase('phone');
    setCode('');
    setCertKey('');
    setOtpActive(false);
    setCodeError('');
    setCodeHint('');
    setAttemptUsed(0);
    setResendSec(0);
    setChangeConfirmOpen(false);
  };

  const requestVerificationCode = async ({ isResend = false } = {}) => {
    if (phoneDigits.length < 10) {
      setCodeError('휴대폰 번호를 입력해주세요.');
      return;
    }

    setSending(true);
    setCodeError('');
    if (!isResend) setCode('');

    try {
      const response = await fetch(`${API_SERVER_DOMAIN}/auth/cert/send`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ telNo: phoneDigits }),
      });
      const data = await response.json();

      if (data.result === 'ok') {
        setCertKey(data.key);
        setPhase('code');
        setOtpActive(true);
        setTimerKey((k) => k + 1);
        setAttemptUsed(0);
        setResendSec(RESEND_COOLDOWN_SEC);
        setCodeHint(isResend ? '새 인증번호를 보냈어요.' : '카카오톡으로 인증번호를 보냈어요');
        setCodeError('');
        return;
      }

      const msg = data.msg || data.message || '인증번호를 보내지 못했어요';
      if (isAlreadyVerifiedError(msg)) {
        setAlreadyUsedOpen(true);
      } else if (/초과|limit|횟수/i.test(msg)) {
        setSendLimitOpen(true);
      } else {
        setNetworkErrorOpen(true);
      }
    } catch (error) {
      console.error(error);
      setNetworkErrorOpen(true);
    } finally {
      setSending(false);
    }
  };

  const verifyCode = async () => {
    if (code.trim().length < 6) {
      setCodeError('인증번호를 입력해주세요.');
      setCodeHint('');
      return;
    }
    if (!otpActive) {
      setCodeError('인증시간이 만료됐어요. 인증번호를 다시 받아주세요.');
      setCodeHint('');
      return;
    }

    setConfirming(true);
    setCodeError('');

    try {
      const response = await fetch(`${API_SERVER_DOMAIN}/auth/cert`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          key: certKey,
          code: code.trim(),
        }),
      });
      const data = await response.json();

      if (data.result === 'ok') {
        markPhoneVerified();
        setPhase('done');
        return;
      }

      const nextUsed = attemptUsed + 1;
      setAttemptUsed(nextUsed);
      setCodeHint('');

      if (nextUsed >= MAX_CODE_ATTEMPTS) {
        setCodeError('인증번호 입력 횟수를 초과했어요. 새 인증번호를 받아주세요.');
        setOtpActive(false);
        setCertKey('');
      } else {
        setCodeError('인증번호가 올바르지 않아요. 다시 확인해주세요.');
      }
    } catch (error) {
      console.error(error);
      setCodeError('네트워크를 확인하고 다시 시도해주세요.');
      setCodeHint('');
    } finally {
      setConfirming(false);
    }
  };

  const onTimerEnd = () => {
    setOtpActive(false);
    setCertKey('');
    setCodeHint('');
    setCodeError('인증시간이 만료됐어요. 인증번호를 다시 받아주세요.');
  };

  const onResendClick = () => {
    if (resendSec > 0 || sending) return;
    requestVerificationCode({ isResend: true });
  };

  const openCustomerCenter = () => {
    window.location.href = 'mailto:help@yaongstore.com';
  };

  if (phase === 'done') {
    return (
      <section className="relative flex h-full min-h-[100dvh] w-full flex-col bg-white font-['Pretendard'] text-black">
        <div className="flex h-full min-h-[100dvh] flex-col px-[10px] pt-2">
          <AuthBackHeader onBack={finishAndContinue} />
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-6 pb-8">
            <SuccessCheckIcon />
            <h1 className="mt-[30px] text-center font-['Pretendard'] text-[22px] font-extrabold leading-[1.4] text-gray05">
              휴대폰 인증이 완료됐어요
            </h1>
            <p className="mt-2.5 text-center font-['Pretendard'] text-[14px] leading-[1.6] text-gray04">
              이제 기프티콘을 구매할 수 있어요.
            </p>
          </div>
          <div className="shrink-0 px-[10px] pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-3">
            <AuthPrimaryButton onClick={finishAndContinue}>기프티콘 구매 계속하기</AuthPrimaryButton>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative flex h-full min-h-[100dvh] w-full flex-col bg-white font-['Pretendard'] text-black">
      <div className="flex h-full min-h-[100dvh] flex-col px-[10px] pt-2">
        <AuthBackHeader onBack={goBack} />

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-[10px] pb-4">
          <h1 className="mt-2 max-w-[272px] font-['Pretendard'] text-[24px] font-bold leading-[1.32] text-black">
            <span className="text-primary-purple-01">기프트콘 구매</span>를 위해
            <br />
            <span className="text-primary-purple-01">휴대폰 번호</span>를 인증해주세요
          </h1>

          <div className="mt-[70px] flex flex-col gap-[30px]">
            <UnderlineField
              label="휴대폰 번호"
              value={phoneNumber}
              onChange={(v) => {
                if (phase === 'code') return;
                setPhoneNumber(formatPhoneDisplay(v));
                setCodeError('');
              }}
              placeholder="'-' 없이 숫자만 입력"
              maxLength={13}
              inputMode="tel"
              autoComplete="tel"
              readOnly={phase === 'code'}
              rightSlot={
                phase === 'code' ? (
                  <button
                    type="button"
                    className="border-0 bg-transparent p-0 font-['Pretendard'] text-[16px] text-primary-purple-01"
                    onClick={() => setChangeConfirmOpen(true)}
                  >
                    번호변경
                  </button>
                ) : null
              }
            />

            {phase === 'code' ? (
              <div>
                <UnderlineField
                  label="인증번호"
                  value={code}
                  onChange={(v) => {
                    setCode(v.replace(/\D/g, '').slice(0, 6));
                    if (codeError) setCodeError('');
                  }}
                  placeholder="6자리 숫자 입력"
                  maxLength={6}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  error={!!codeError}
                  rightSlot={
                    otpActive ? (
                      <span className="tabular-nums">
                        <Timer key={timerKey} minute={OTP_MINUTES} onEnd={onTimerEnd} />
                      </span>
                    ) : (
                      <span className="tabular-nums text-gray03">00:00</span>
                    )
                  }
                />

                {codeError ? (
                  <div className="mt-2.5 flex items-start gap-2 px-1">
                    <ErrorWarnIcon className="mt-0.5 h-4 w-4 shrink-0" />
                    <p className="font-['Pretendard'] text-[12px] leading-[1.32] text-[#F6475F]">
                      {codeError}
                    </p>
                  </div>
                ) : codeHint ? (
                  <div className="mt-2.5 flex items-center gap-2 px-1">
                    <HintCheckIcon />
                    <p className="font-['Pretendard'] text-[12px] text-primary-purple-01">{codeHint}</p>
                  </div>
                ) : null}

                {codeError && attemptUsed > 0 && remainingAttempts > 0 && otpActive ? (
                  <p className="mt-1.5 text-right font-['Pretendard'] text-[12px] text-gray04">
                    남은 입력 {remainingAttempts}회
                  </p>
                ) : null}

                <div className="mt-[30px] flex items-center justify-between">
                  <button
                    type="button"
                    className="border-0 bg-transparent p-0 font-['Pretendard'] text-[12px] text-gray05 underline"
                    onClick={() => setHelpOpen(true)}
                  >
                    인증번호가 오지 않나요?
                  </button>

                  {resendSec > 0 ? (
                    <span className="font-['Pretendard'] text-[12px] text-gray03">
                      {formatMmSs(resendSec)} 후 재전송
                    </span>
                  ) : (
                    <button
                      type="button"
                      disabled={sending}
                      onClick={onResendClick}
                      className="rounded-[5px] border border-solid border-primary-purple-01 bg-[#F0ECFF] px-4 py-2.5 font-['Pretendard'] text-[12px] font-bold text-primary-purple-01 disabled:opacity-50"
                    >
                      인증번호 재전송
                    </button>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="shrink-0 px-[10px] pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-3">
          {phase === 'phone' ? (
            <>
              <p className="mb-4 px-2.5 font-['Pretendard'] text-[12px] leading-[1.45] text-gray04">
                {PRIVACY_NOTE}
              </p>
              <AuthPrimaryButton
                disabled={!canSend}
                onClick={() => requestVerificationCode({ isResend: false })}
              >
                {sending ? '전송 중...' : '인증번호 받기'}
              </AuthPrimaryButton>
            </>
          ) : (
            <AuthPrimaryButton disabled={!canConfirm} onClick={verifyCode}>
              {confirming ? '인증 중...' : '인증하기'}
            </AuthPrimaryButton>
          )}
        </div>
      </div>

      <CertHelpSheet open={helpOpen} onClose={() => setHelpOpen(false)} />

      <CertDialog
        open={changeConfirmOpen}
        icon={<PhoneBadgeIcon />}
        title="휴대폰 번호를 변경할까요?"
        description={'번호를 변경하면 현재 인증번호와\n남은 시간이 초기화돼요.'}
        secondaryText="계속 인증"
        primaryText="번호 변경"
        onSecondary={() => setChangeConfirmOpen(false)}
        onPrimary={resetToPhoneStep}
      />

      <CertDialog
        open={alreadyUsedOpen}
        icon={<UserBadgeIcon />}
        title="이미 인증된 휴대폰 번호예요"
        description={'다른 야옹상회 계정에 연결된 번호예요.\n본인 계정을 확인해주세요.'}
        secondaryText="닫기"
        primaryText="고객센터"
        onSecondary={() => setAlreadyUsedOpen(false)}
        onPrimary={() => {
          setAlreadyUsedOpen(false);
          openCustomerCenter();
        }}
      />

      <CertDialog
        open={networkErrorOpen}
        icon={<NetworkErrorIcon />}
        title="인증번호를 보내지 못했어요"
        description="네트워크를 확인하고 다시 시도해주세요."
        secondaryText="닫기"
        primaryText="다시 시도"
        onSecondary={() => setNetworkErrorOpen(false)}
        onPrimary={() => {
          setNetworkErrorOpen(false);
          requestVerificationCode({ isResend: phase === 'code' });
        }}
      />

      <CertDialog
        open={sendLimitOpen}
        icon={<NetworkErrorIcon />}
        title="인증번호 요청 횟수를 초과했어요"
        description="잠시 후 다시 시도해주세요."
        secondaryText="닫기"
        onSecondary={() => setSendLimitOpen(false)}
      />
    </section>
  );
}
